import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogContent,
  DialogActions,
  TextField,
  LinearProgress,
  Autocomplete,
  Snackbar,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Divider,
  Tooltip,
} from '@mui/material';
import { DataGrid, GridRenderCellParams, GridColDef } from '@mui/x-data-grid';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  ShoppingCart as ShoppingCartIcon,
  Inventory as InventoryIcon,
  Paid as PaidIcon,
  TrendingUp as TrendingUpIcon,
  LocalShipping as LocalShippingIcon,
  Remove as RemoveIcon,
  HourglassEmpty as HourglassIcon,
} from '@mui/icons-material';

import {
  useBirdProcurements,
  useCreateBirdProcurement,
  useDeleteBirdProcurement,
  useMarkBirdProcurementAsDelivered,
  useBirdProcurementsStats,
  useSuppliers,
  useBirdTypes,
} from '../hooks/useBirds';
import { birdProcurementsService } from '../services/apiServices';
import { BirdProcurement } from '../types';

interface BirdEntry {
  supplier_id: string;
  supplier_name: string;
  color: 'white' | 'brown' | '';
  birds_type: string;
  quantity_bought: number;
  unit_price: string;
}

interface DeliveryBird {
  bird_id: number;
  color: string;
  birds_type: string;
  supplier_name: string;
  quantity_bought: number;
  quantity_delivered: number;
  date_delivered: string;
}

const emptyBirdEntry = (): BirdEntry => ({
  supplier_id: '',
  supplier_name: '',
  color: '',
  birds_type: '',
  quantity_bought: 0,
  unit_price: '',
});

const formatDate = (dateString: string): string => {
  if (!dateString) return '—';
  const [y, m, d] = dateString.split('-');
  const months = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];
  return `${parseInt(d)} ${months[parseInt(m) - 1]} ${y}`;
};

const parseCurrency = (value: string): number =>
  parseFloat(value?.replace(/GH₵\s?|,/g, '') ?? '0') || 0;

const StatusChip = ({ status }: { status?: string }) => {
  if (status === 'delivered') {
    return (
      <Chip
        icon={
          <CheckCircleIcon sx={{ fontSize: '0.75rem !important', color: '#4caf50 !important' }} />
        }
        label="Delivered"
        size="small"
        sx={{
          height: 22,
          fontSize: '0.65rem',
          fontWeight: 700,
          bgcolor: 'rgba(76,175,80,0.12)',
          color: '#4caf50',
          border: '1px solid rgba(76,175,80,0.3)',
          '& .MuiChip-icon': { ml: '4px' },
        }}
      />
    );
  }
  if (status === 'partially_delivered') {
    return (
      <Chip
        icon={
          <LocalShippingIcon sx={{ fontSize: '0.75rem !important', color: '#2196f3 !important' }} />
        }
        label="Partial"
        size="small"
        sx={{
          height: 22,
          fontSize: '0.65rem',
          fontWeight: 700,
          bgcolor: 'rgba(33,150,243,0.12)',
          color: '#42a5f5',
          border: '1px solid rgba(33,150,243,0.3)',
          '& .MuiChip-icon': { ml: '4px' },
        }}
      />
    );
  }
  return (
    <Chip
      icon={<HourglassIcon sx={{ fontSize: '0.75rem !important', color: '#9e9e9e !important' }} />}
      label="Pending"
      size="small"
      sx={{
        height: 22,
        fontSize: '0.65rem',
        fontWeight: 700,
        bgcolor: 'rgba(158,158,158,0.1)',
        color: '#9e9e9e',
        border: '1px solid rgba(158,158,158,0.2)',
        '& .MuiChip-icon': { ml: '4px' },
      }}
    />
  );
};

const ProcurementsPage: React.FC = () => {
  const navigate = useNavigate();
  const [addDialog, setAddDialog] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [deliveryDialog, setDeliveryDialog] = useState(false);
  const [procurementToDeliver, setProcurementToDeliver] = useState<BirdProcurement | null>(null);
  const [deliveryBirds, setDeliveryBirds] = useState<DeliveryBird[]>([]);

  const [headerData, setHeaderData] = useState({
    identifier: '',
    date_procured: '',
  });
  const [birdEntries, setBirdEntries] = useState<BirdEntry[]>([emptyBirdEntry()]);

  const [procurementToDelete, setProcurementToDelete] = useState<{
    id: number;
    name: string;
  } | null>(null);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 15 });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'warning',
  });

  const {
    data: procurementsData,
    isLoading,
    error,
    refetch,
    isFetching,
  } = useBirdProcurements({
    page: paginationModel.page + 1,
    per_page: paginationModel.pageSize,
  });

  const { data: suppliers } = useSuppliers();
  const { data: birdTypes } = useBirdTypes();
  const { data: stats, isFetching: isStatsFetching } = useBirdProcurementsStats();
  const createMutation = useCreateBirdProcurement();
  const deleteMutation = useDeleteBirdProcurement();
  const markAsDeliveredMutation = useMarkBirdProcurementAsDelivered();

  const showSnackbar = (message: string, severity: 'success' | 'error' | 'warning' = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleView = (id: number) => navigate(`/procurements/${id}`);

  const handleOpenAddDialog = () => {
    setAddDialog(true);
    setHeaderData({ identifier: '', date_procured: '' });
    setBirdEntries([emptyBirdEntry()]);
  };

  const addBirdEntry = () => setBirdEntries([...birdEntries, emptyBirdEntry()]);
  const removeBirdEntry = (index: number) =>
    setBirdEntries(birdEntries.filter((_, i) => i !== index));
  const updateBirdEntry = (index: number, field: keyof BirdEntry, value: string | number) => {
    const updated = [...birdEntries];
    updated[index] = { ...updated[index], [field]: value };
    setBirdEntries(updated);
  };

  const overallTotal = birdEntries.reduce(
    (sum, e) => sum + e.quantity_bought * (parseFloat(e.unit_price) || 0),
    0
  );
  const totalBirdsInForm = birdEntries.reduce((sum, e) => sum + e.quantity_bought, 0);

  const handleCreateProcurement = async () => {
    if (!headerData.identifier.trim()) {
      showSnackbar('Please enter a batch identifier', 'warning');
      return;
    }
    if (!headerData.date_procured) {
      showSnackbar('Please select the procurement date', 'warning');
      return;
    }
    const validBirds = birdEntries.filter(
      (e) =>
        (e.supplier_id || e.supplier_name.trim()) &&
        e.color &&
        e.birds_type &&
        e.quantity_bought > 0 &&
        parseFloat(e.unit_price) > 0
    );
    if (validBirds.length === 0) {
      showSnackbar('Please add at least one complete bird strain entry', 'warning');
      return;
    }
    try {
      // Resolve new supplier names: match existing first, then create — deduplicated
      const supplierCache: Record<string, number> = {};
      const resolvedBirds = await Promise.all(
        validBirds.map(async (e) => {
          let supplierId = e.supplier_id ? parseInt(e.supplier_id) : null;
          if (!supplierId && e.supplier_name.trim()) {
            const key = e.supplier_name.trim().toLowerCase();
            if (supplierCache[key]) {
              supplierId = supplierCache[key];
            } else {
              const existing = (suppliers ?? []).find((s) => s.name.toLowerCase() === key);
              if (existing) {
                supplierId = existing.id;
              } else {
                const created = await birdProcurementsService.createSupplier(
                  e.supplier_name.trim()
                );
                supplierId = created.id;
              }
              supplierCache[key] = supplierId!;
            }
          }
          return {
            supplier_id: supplierId,
            color: e.color as 'white' | 'brown',
            birds_type: e.birds_type,
            quantity_bought: e.quantity_bought,
            unit_price: parseFloat(e.unit_price),
          };
        })
      );
      await createMutation.mutateAsync({
        identifier: headerData.identifier.trim(),
        date_procured: headerData.date_procured,
        birds: resolvedBirds,
      } as any);
      showSnackbar('Bird procurement created successfully!');
      setAddDialog(false);
    } catch {
      showSnackbar('Failed to create procurement. Please try again.', 'error');
    }
  };

  const handleConfirmDelete = async () => {
    if (!procurementToDelete) return;
    try {
      await deleteMutation.mutateAsync(procurementToDelete.id);
      setDeleteDialog(false);
      setProcurementToDelete(null);
    } catch {
      showSnackbar('Failed to delete procurement.', 'error');
    }
  };

  const handleMarkAsDeliveredClick = (procurement: BirdProcurement) => {
    const today = new Date().toISOString().split('T')[0];
    setProcurementToDeliver(procurement);
    setDeliveryBirds(
      (procurement.birds || []).map((bird) => ({
        bird_id: bird.id,
        color: bird.color,
        birds_type: bird.birds_type,
        supplier_name: bird.supplier?.name || `Supplier #${bird.supplier_id}`,
        quantity_bought: bird.quantity_bought,
        quantity_delivered: bird.quantity_bought,
        date_delivered: bird.date_delivered ?? today,
      }))
    );
    setDeliveryDialog(true);
  };

  const handleCloseDeliveryDialog = () => {
    setDeliveryDialog(false);
    setProcurementToDeliver(null);
    setDeliveryBirds([]);
  };

  const handleConfirmDelivery = async () => {
    if (!procurementToDeliver) return;
    const birdsToDeliver = deliveryBirds.filter(
      (b) => b.quantity_delivered > 0 && b.date_delivered
    );
    if (birdsToDeliver.length === 0) {
      showSnackbar('Please enter quantity and delivery date for at least one strain', 'warning');
      return;
    }
    try {
      await markAsDeliveredMutation.mutateAsync({
        id: procurementToDeliver.id,
        birds: birdsToDeliver.map((b) => ({
          bird_id: b.bird_id,
          quantity_delivered: b.quantity_delivered,
          date_delivered: b.date_delivered,
        })),
      });
      showSnackbar('Delivery recorded successfully!');
      handleCloseDeliveryDialog();
    } catch {
      showSnackbar('Failed to record delivery. Please try again.', 'error');
    }
  };

  const procurements = procurementsData?.data || [];
  const paginationMeta = procurementsData?.meta || {
    total: 0,
    current_page: 1,
    last_page: 1,
    per_page: 15,
  };

  const totalProcurements = paginationMeta.total;
  const totalBirds = procurements.reduce((s: number, p: any) => s + p.total_quantity, 0);
  const totalCost = procurements.reduce((s: number, p: any) => s + parseCurrency(p.total_cost), 0);
  const avgCost = totalBirds > 0 ? totalCost / totalBirds : 0;
  const deliveredCount = procurements.filter((p: any) => p.status === 'delivered').length;

  const columns: GridColDef[] = [
    {
      field: 'identifier',
      headerName: 'Batch',
      flex: 1.2,
      minWidth: 160,
      renderCell: (params: GridRenderCellParams) => {
        const status = params.row.status;
        const dot =
          status === 'delivered'
            ? '#4caf50'
            : status === 'partially_delivered'
              ? '#42a5f5'
              : 'rgba(255,255,255,0.2)';
        return (
          <Box display="flex" alignItems="center" gap={1.2} height="100%">
            <Box sx={{ width: 6, height: 28, borderRadius: 1, bgcolor: dot, flexShrink: 0 }} />
            <Typography
              variant="body2"
              fontWeight={600}
              sx={{ color: '#ffffff', fontSize: '0.83rem', lineHeight: 1.2 }}
            >
              {params.value}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      minWidth: 120,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => (
        <Box display="flex" alignItems="center" height="100%">
          <StatusChip status={params.value} />
        </Box>
      ),
    },
    {
      field: 'date_procured',
      headerName: 'Procured',
      flex: 0.9,
      minWidth: 115,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.8rem' }}>
          {formatDate(params.value)}
        </Typography>
      ),
    },
    {
      field: 'date_delivered',
      headerName: 'Delivered',
      flex: 0.9,
      minWidth: 115,
      renderCell: (params: GridRenderCellParams) => (
        <Typography
          variant="body2"
          sx={{
            color: params.value ? 'rgba(255,255,255,0.75)' : 'rgba(255,255,255,0.25)',
            fontSize: '0.8rem',
            fontStyle: params.value ? 'normal' : 'italic',
          }}
        >
          {params.value ? formatDate(params.value) : 'pending'}
        </Typography>
      ),
    },
    {
      field: 'total_quantity',
      headerName: 'Birds',
      flex: 0.7,
      minWidth: 90,
      type: 'number',
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2" fontWeight={600} sx={{ color: '#66bb6a', fontSize: '0.83rem' }}>
          {params.value?.toLocaleString()}
        </Typography>
      ),
    },
    {
      field: 'birds',
      headerName: 'Strains',
      flex: 0.6,
      minWidth: 80,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => {
        const count = (params.value || []).length;
        return (
          <Chip
            label={count}
            size="small"
            sx={{
              height: 20,
              fontSize: '0.65rem',
              fontWeight: 700,
              bgcolor: 'rgba(255,152,0,0.1)',
              color: 'rgba(255,152,0,0.8)',
              border: '1px solid rgba(255,152,0,0.2)',
            }}
          />
        );
      },
    },
    {
      field: 'total_cost',
      headerName: 'Total Cost',
      flex: 1,
      minWidth: 130,
      renderCell: (params: GridRenderCellParams) => (
        <Typography
          variant="body2"
          fontWeight={700}
          sx={{ color: '#42a5f5', fontSize: '0.83rem', fontFamily: 'monospace' }}
        >
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'supplier',
      headerName: 'Supplier(s)',
      flex: 1.1,
      minWidth: 140,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => {
        const birds = params.row.birds || [];
        const seen = new Set<string>();
        const names: string[] = [];
        birds.forEach((b: any) => {
          const n = b.supplier?.name;
          if (n && !seen.has(n)) {
            seen.add(n);
            names.push(n);
          }
        });
        return (
          <Typography
            variant="body2"
            sx={{
              color: names.length > 0 ? 'rgba(255,255,255,0.7)' : 'rgba(255,255,255,0.2)',
              fontSize: '0.8rem',
              fontStyle: names.length === 0 ? 'italic' : 'normal',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {names.length > 0 ? names.join(', ') : '—'}
          </Typography>
        );
      },
    },
    {
      field: 'actions',
      headerName: '',
      width: 110,
      minWidth: 110,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => (
        <Box display="flex" alignItems="center" gap={0.25} height="100%">
          <Tooltip title="View details" arrow>
            <IconButton
              size="small"
              onClick={() => handleView(params.row.id)}
              sx={{
                color: 'rgba(255,152,0,0.7)',
                '&:hover': { color: '#ff9800', bgcolor: 'rgba(255,152,0,0.1)' },
              }}
            >
              <ViewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {params.row.status !== 'delivered' && (
            <Tooltip
              title={
                params.row.status === 'partially_delivered'
                  ? 'Record more deliveries'
                  : 'Mark as delivered'
              }
              arrow
            >
              <span>
                <IconButton
                  size="small"
                  onClick={() => handleMarkAsDeliveredClick(params.row)}
                  disabled={markAsDeliveredMutation.isPending}
                  sx={{
                    color:
                      params.row.status === 'partially_delivered'
                        ? 'rgba(33,150,243,0.7)'
                        : 'rgba(76,175,80,0.7)',
                    '&:hover': {
                      color: params.row.status === 'partially_delivered' ? '#42a5f5' : '#4caf50',
                      bgcolor:
                        params.row.status === 'partially_delivered'
                          ? 'rgba(33,150,243,0.1)'
                          : 'rgba(76,175,80,0.1)',
                    },
                  }}
                >
                  <CheckCircleIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          )}
          <Tooltip title="Delete" arrow>
            <span>
              <IconButton
                size="small"
                onClick={() => {
                  setProcurementToDelete({ id: params.row.id, name: params.row.identifier });
                  setDeleteDialog(true);
                }}
                disabled={deleteMutation.isPending}
                sx={{
                  color: 'rgba(244,67,54,0.6)',
                  '&:hover': { color: '#f44336', bgcolor: 'rgba(244,67,54,0.1)' },
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      ),
    },
  ];

  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <Box textAlign="center" maxWidth={480}>
          <Box
            sx={{
              width: 72,
              height: 72,
              borderRadius: 3,
              mx: 'auto',
              mb: 3,
              bgcolor: 'rgba(244,67,54,0.1)',
              border: '1px solid rgba(244,67,54,0.25)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <WarningIcon sx={{ color: '#f44336', fontSize: 36 }} />
          </Box>
          <Typography variant="h6" fontWeight={700} sx={{ color: '#ffffff', mb: 1 }}>
            Failed to load procurements
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)', mb: 3 }}>
            {error instanceof Error ? error.message : 'An unexpected error occurred.'}
          </Typography>
          <Button
            variant="outlined"
            onClick={() => refetch()}
            sx={{
              borderColor: 'rgba(244,67,54,0.4)',
              color: '#f44336',
              borderRadius: 2,
              px: 4,
              textTransform: 'none',
              fontWeight: 600,
              '&:hover': { borderColor: '#f44336', bgcolor: 'rgba(244,67,54,0.08)' },
            }}
          >
            Try again
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {(isFetching || isStatsFetching) && (
        <LinearProgress
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 9999,
            height: 2,
            bgcolor: 'transparent',
            '& .MuiLinearProgress-bar': {
              background: 'linear-gradient(90deg, #ff9800 0%, #ffcc02 50%, #ff9800 100%)',
            },
          }}
        />
      )}

      {/* ── Page Header ── */}
      <Box
        sx={{
          mb: 4,
          borderRadius: 3,
          background: 'linear-gradient(135deg, #0e0e10 0%, #1a1a1d 60%, #0e0e10 100%)',
          border: '1px solid rgba(255,152,0,0.12)',
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: -60,
            right: -60,
            width: 220,
            height: 220,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(255,152,0,0.07) 0%, transparent 65%)',
            pointerEvents: 'none',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: -40,
            left: 80,
            width: 150,
            height: 150,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(33,150,243,0.04) 0%, transparent 65%)',
            pointerEvents: 'none',
          }}
        />
        <Box
          sx={{
            p: { xs: 2.5, md: 3 },
            display: 'flex',
            alignItems: 'center',
            gap: 2.5,
            position: 'relative',
          }}
        >
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: 2.5,
              flexShrink: 0,
              background:
                'linear-gradient(135deg, rgba(255,152,0,0.18) 0%, rgba(255,152,0,0.06) 100%)',
              border: '1px solid rgba(255,152,0,0.22)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <ShoppingCartIcon sx={{ color: '#ff9800', fontSize: 28 }} />
          </Box>
          <Box flex={1}>
            <Typography
              variant="h4"
              fontWeight={800}
              sx={{
                color: '#ffffff',
                letterSpacing: '-0.02em',
                lineHeight: 1.1,
                fontSize: { xs: '1.5rem', md: '1.9rem' },
              }}
            >
              Bird Procurements
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.38)', mt: 0.4 }}>
              Manage supplier orders, batch deliveries and procurement costs
            </Typography>
          </Box>
          <Button
            startIcon={<AddIcon />}
            onClick={handleOpenAddDialog}
            variant="contained"
            sx={{
              background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)',
              color: '#ffffff',
              borderRadius: 2,
              px: { xs: 2, md: 3 },
              py: 1.2,
              textTransform: 'none',
              fontWeight: 700,
              fontSize: '0.875rem',
              boxShadow: '0 4px 18px rgba(255,152,0,0.32)',
              flexShrink: 0,
              '&:hover': {
                background: 'linear-gradient(135deg, #ffa726 0%, #ff9800 100%)',
                boxShadow: '0 6px 22px rgba(255,152,0,0.42)',
              },
            }}
          >
            New Procurement
          </Button>
        </Box>
      </Box>

      {/* ── Stats Row ── */}
      <Box display="flex" gap={2} mb={4} flexWrap="wrap">
        {[
          {
            label: 'Total Procurements',
            value: stats?.total_count ?? totalProcurements,
            sub: `${deliveredCount} delivered`,
            color: '#ff9800',
            Icon: ShoppingCartIcon,
          },
          {
            label: 'Total Birds',
            value: (stats?.total_quantity ?? totalBirds).toLocaleString(),
            sub: 'birds procured',
            color: '#66bb6a',
            Icon: InventoryIcon,
          },
          {
            label: 'Total Investment',
            value: `GH₵ ${totalCost.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
            sub: 'total spend',
            color: '#42a5f5',
            Icon: PaidIcon,
          },
          {
            label: 'Avg Cost / Bird',
            value: `GH₵ ${avgCost.toFixed(2)}`,
            sub: 'cost efficiency',
            color: '#ba68c8',
            Icon: TrendingUpIcon,
          },
        ].map(({ label, value, sub, color, Icon }) => (
          <Paper
            key={label}
            elevation={0}
            sx={{
              flex: 1,
              minWidth: 180,
              p: 2.5,
              background: '#111318',
              border: '1px solid rgba(255,255,255,0.05)',
              borderLeft: `3px solid ${color}`,
              borderRadius: 2.5,
              position: 'relative',
              overflow: 'hidden',
              cursor: 'default',
              transition: 'transform 0.18s ease, box-shadow 0.18s ease',
              '&:hover': {
                transform: 'translateY(-3px)',
                boxShadow: `0 12px 36px rgba(0,0,0,0.55), 0 0 0 1px ${color}22`,
              },
            }}
          >
            <Icon
              sx={{
                position: 'absolute',
                right: 14,
                top: '50%',
                transform: 'translateY(-50%)',
                fontSize: 56,
                color: `${color}0D`,
              }}
            />
            <Typography
              variant="caption"
              sx={{
                color: 'rgba(255,255,255,0.35)',
                letterSpacing: '0.7px',
                fontSize: '0.62rem',
                textTransform: 'uppercase',
                display: 'block',
                mb: 0.75,
              }}
            >
              {label}
            </Typography>
            <Typography
              variant="h5"
              fontWeight={800}
              sx={{
                color,
                lineHeight: 1,
                mb: 0.5,
                fontSize:
                  typeof value === 'string' && value.startsWith('GH') ? '1.05rem' : '1.6rem',
              }}
            >
              {value}
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: 'rgba(255,255,255,0.25)', fontSize: '0.68rem' }}
            >
              {sub}
            </Typography>
          </Paper>
        ))}
      </Box>

      {/* ── Procurement Table ── */}
      <Paper
        elevation={0}
        sx={{
          border: '1px solid rgba(255,152,0,0.12)',
          borderRadius: 2.5,
          overflow: 'hidden',
          background: '#0e0e10',
        }}
      >
        {/* Table header bar */}
        <Box
          sx={{
            px: 3,
            py: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            background: 'linear-gradient(90deg, rgba(255,152,0,0.06) 0%, transparent 100%)',
            borderBottom: '1px solid rgba(255,152,0,0.1)',
          }}
        >
          <LocalShippingIcon sx={{ color: '#ff9800', fontSize: '1rem' }} />
          <Typography
            variant="subtitle2"
            fontWeight={700}
            sx={{ color: '#ffffff', letterSpacing: '0.3px' }}
          >
            Procurement Records
          </Typography>
          <Chip
            label={paginationMeta.total}
            size="small"
            sx={{
              height: 19,
              fontSize: '0.62rem',
              fontWeight: 700,
              bgcolor: 'rgba(255,152,0,0.1)',
              color: '#ff9800',
              border: '1px solid rgba(255,152,0,0.2)',
            }}
          />
        </Box>

        <DataGrid
          rows={procurements}
          columns={columns}
          rowCount={paginationMeta.total}
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 15, 25, 50]}
          disableRowSelectionOnClick
          loading={isLoading}
          rowHeight={52}
          sx={{
            border: 0,
            background: 'transparent',
            '& .MuiDataGrid-cell': {
              borderBottom: '1px solid rgba(255,255,255,0.04)',
              color: '#ffffff',
              display: 'flex',
              alignItems: 'center',
            },
            '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': { outline: 'none' },
            '& .MuiDataGrid-columnHeaders': {
              background: 'rgba(255,152,0,0.05)',
              borderBottom: '1px solid rgba(255,152,0,0.12)',
            },
            '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': {
              outline: 'none',
            },
            '& .MuiDataGrid-columnHeaderTitle': {
              fontWeight: 700,
              fontSize: '0.68rem',
              letterSpacing: '0.7px',
              color: 'rgba(255,152,0,0.6)',
              textTransform: 'uppercase',
            },
            '& .MuiDataGrid-row:hover': { background: 'rgba(255,152,0,0.04)' },
            '& .MuiDataGrid-row:nth-of-type(even)': { background: 'rgba(255,255,255,0.012)' },
            '& .MuiDataGrid-footerContainer': {
              borderTop: '1px solid rgba(255,152,0,0.1)',
              background: 'rgba(255,152,0,0.03)',
            },
            '& .MuiTablePagination-root, & .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows':
              {
                color: 'rgba(255,255,255,0.5)',
                fontSize: '0.78rem',
              },
            '& .MuiTablePagination-select': { color: 'rgba(255,255,255,0.7)' },
            '& .MuiIconButton-root': { color: 'rgba(255,255,255,0.4)' },
            '& .MuiIconButton-root.Mui-disabled': { color: 'rgba(255,255,255,0.15)' },
            '& .MuiDataGrid-virtualScroller': { minHeight: 200 },
          }}
        />
      </Paper>

      {/* ══════════════════════════════════════
          Add Procurement Dialog
      ══════════════════════════════════════ */}
      <Dialog
        open={addDialog}
        onClose={() => setAddDialog(false)}
        maxWidth="md"
        fullWidth
        slotProps={{
          paper: {
            sx: {
              borderRadius: 3.5,
              background: '#0e0e10',
              border: '1px solid rgba(255,152,0,0.2)',
              backgroundImage: 'none',
            },
          },
        }}
      >
        {/* Dialog header */}
        <Box
          sx={{
            px: 3,
            pt: 3,
            pb: 2.5,
            borderBottom: '1px solid rgba(255,152,0,0.12)',
            background: 'linear-gradient(135deg, rgba(255,152,0,0.06) 0%, transparent 100%)',
            display: 'flex',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: 2,
              bgcolor: 'rgba(255,152,0,0.12)',
              border: '1px solid rgba(255,152,0,0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <ShoppingCartIcon sx={{ color: '#ff9800', fontSize: 22 }} />
          </Box>
          <Box>
            <Typography
              variant="h6"
              fontWeight={800}
              sx={{ color: '#ffffff', letterSpacing: '-0.01em' }}
            >
              New Procurement
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.38)' }}>
              Create a batch with one or more bird strains
            </Typography>
          </Box>
        </Box>

        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ p: 3, maxHeight: '58vh', overflowY: 'auto' }}>
            <Box display="flex" flexDirection="column" gap={2.5}>
              {/* Batch details */}
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'rgba(255,255,255,0.35)',
                    letterSpacing: '0.7px',
                    fontSize: '0.62rem',
                    textTransform: 'uppercase',
                    display: 'block',
                    mb: 1.5,
                  }}
                >
                  Batch Details
                </Typography>
                <Box display="flex" flexDirection="column" gap={2}>
                  <TextField
                    label="Batch Identifier"
                    fullWidth
                    required
                    value={headerData.identifier}
                    onChange={(e) => setHeaderData({ ...headerData, identifier: e.target.value })}
                    placeholder="e.g., Eighth Batch"
                    helperText="A unique name for this procurement batch"
                    size="small"
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                  />
                  <TextField
                    label="Date Procured"
                    type="date"
                    fullWidth
                    required
                    size="small"
                    value={headerData.date_procured}
                    onChange={(e) =>
                      setHeaderData({ ...headerData, date_procured: e.target.value })
                    }
                    slotProps={{ inputLabel: { shrink: true } }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                  />
                </Box>
              </Box>

              <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)' }} />

              {/* Bird strains */}
              <Box>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5}>
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'rgba(255,255,255,0.35)',
                        letterSpacing: '0.7px',
                        fontSize: '0.62rem',
                        textTransform: 'uppercase',
                        display: 'block',
                      }}
                    >
                      Bird Strains
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: 'rgba(255,255,255,0.25)', fontSize: '0.7rem' }}
                    >
                      {birdEntries.length} strain{birdEntries.length !== 1 ? 's' : ''} added
                    </Typography>
                  </Box>
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={addBirdEntry}
                    sx={{
                      color: '#ff9800',
                      border: '1px solid rgba(255,152,0,0.3)',
                      borderRadius: 1.5,
                      textTransform: 'none',
                      fontWeight: 600,
                      fontSize: '0.78rem',
                      px: 1.5,
                      py: 0.5,
                      '&:hover': {
                        border: '1px solid rgba(255,152,0,0.6)',
                        bgcolor: 'rgba(255,152,0,0.06)',
                      },
                    }}
                  >
                    Add Strain
                  </Button>
                </Box>

                <Box display="flex" flexDirection="column" gap={1.5}>
                  {birdEntries.map((entry, index) => {
                    const subtotal = entry.quantity_bought * (parseFloat(entry.unit_price) || 0);
                    const accent =
                      entry.color === 'white'
                        ? '#42a5f5'
                        : entry.color === 'brown'
                          ? '#a1887f'
                          : '#ff9800';
                    const borderColor =
                      entry.color === 'white'
                        ? 'rgba(66,165,245,0.2)'
                        : entry.color === 'brown'
                          ? 'rgba(161,136,127,0.2)'
                          : 'rgba(255,152,0,0.14)';

                    return (
                      <Paper
                        key={index}
                        elevation={0}
                        sx={{
                          p: 2.5,
                          border: `1px solid ${borderColor}`,
                          borderLeft: `3px solid ${accent}`,
                          borderRadius: 2,
                          background: 'rgba(255,255,255,0.015)',
                        }}
                      >
                        <Box
                          display="flex"
                          alignItems="center"
                          justifyContent="space-between"
                          mb={2}
                        >
                          <Box display="flex" alignItems="center" gap={1}>
                            <Box
                              sx={{
                                width: 22,
                                height: 22,
                                borderRadius: 1,
                                bgcolor: `${accent}20`,
                                border: `1px solid ${accent}40`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            >
                              <Typography
                                sx={{ fontSize: '0.65rem', fontWeight: 800, color: accent }}
                              >
                                {index + 1}
                              </Typography>
                            </Box>
                            <Typography
                              variant="caption"
                              fontWeight={700}
                              sx={{ color: accent, letterSpacing: '0.3px' }}
                            >
                              {entry.color
                                ? `${entry.color.charAt(0).toUpperCase() + entry.color.slice(1)}${entry.birds_type ? ` · ${entry.birds_type}` : ''}`
                                : `Strain ${index + 1}`}
                            </Typography>
                          </Box>
                          {birdEntries.length > 1 && (
                            <Tooltip title="Remove strain" arrow>
                              <IconButton
                                size="small"
                                onClick={() => removeBirdEntry(index)}
                                sx={{
                                  color: 'rgba(244,67,54,0.5)',
                                  width: 28,
                                  height: 28,
                                  '&:hover': { color: '#f44336', bgcolor: 'rgba(244,67,54,0.08)' },
                                }}
                              >
                                <RemoveIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>

                        <Box display="flex" gap={1.5} mb={1.5} flexWrap="wrap">
                          <Autocomplete
                            sx={{ flex: 1, minWidth: 155 }}
                            size="small"
                            freeSolo
                            options={suppliers || []}
                            getOptionLabel={(o) => (typeof o === 'string' ? o : o.name)}
                            value={
                              suppliers?.find((s) => s.id.toString() === entry.supplier_id) ||
                              entry.supplier_name ||
                              null
                            }
                            onChange={(_, v) => {
                              const updated = [...birdEntries];
                              if (v && typeof v === 'object') {
                                updated[index] = {
                                  ...updated[index],
                                  supplier_id: v.id.toString(),
                                  supplier_name: v.name,
                                };
                              } else {
                                updated[index] = {
                                  ...updated[index],
                                  supplier_id: '',
                                  supplier_name: typeof v === 'string' ? v : '',
                                };
                              }
                              setBirdEntries(updated);
                            }}
                            onInputChange={(_, val, reason) => {
                              if (reason === 'input') {
                                const updated = [...birdEntries];
                                updated[index] = {
                                  ...updated[index],
                                  supplier_id: '',
                                  supplier_name: val,
                                };
                                setBirdEntries(updated);
                              }
                            }}
                            renderInput={(p) => (
                              <TextField
                                {...p}
                                label="Supplier"
                                required
                                helperText={
                                  !entry.supplier_id && entry.supplier_name
                                    ? 'Will be created'
                                    : undefined
                                }
                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                              />
                            )}
                          />
                          <FormControl sx={{ minWidth: 110 }} size="small" required>
                            <InputLabel>Color</InputLabel>
                            <Select
                              label="Color"
                              value={entry.color}
                              onChange={(e) => updateBirdEntry(index, 'color', e.target.value)}
                              sx={{ borderRadius: 1.5 }}
                            >
                              <MenuItem value="white">⬜ White</MenuItem>
                              <MenuItem value="brown">🟫 Brown</MenuItem>
                            </Select>
                          </FormControl>
                          <Autocomplete
                            sx={{ flex: 1.2, minWidth: 185 }}
                            size="small"
                            options={birdTypes || []}
                            getOptionLabel={(o) => o}
                            value={entry.birds_type || null}
                            onChange={(_, v) => updateBirdEntry(index, 'birds_type', v || '')}
                            renderInput={(p) => (
                              <TextField
                                {...p}
                                label="Breed"
                                required
                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                              />
                            )}
                          />
                        </Box>

                        <Box display="flex" gap={1.5} flexWrap="wrap">
                          <TextField
                            label="Quantity"
                            type="number"
                            size="small"
                            sx={{
                              flex: 1,
                              minWidth: 110,
                              '& .MuiOutlinedInput-root': { borderRadius: 1.5 },
                            }}
                            value={entry.quantity_bought}
                            onChange={(e) =>
                              updateBirdEntry(
                                index,
                                'quantity_bought',
                                parseInt(e.target.value) || 0
                              )
                            }
                            slotProps={{ htmlInput: { min: 0 } }}
                            required
                          />
                          <TextField
                            label="Unit Price (GH₵)"
                            type="number"
                            size="small"
                            sx={{
                              flex: 1,
                              minWidth: 130,
                              '& .MuiOutlinedInput-root': { borderRadius: 1.5 },
                            }}
                            value={entry.unit_price}
                            onChange={(e) => updateBirdEntry(index, 'unit_price', e.target.value)}
                            slotProps={{ htmlInput: { min: 0, step: 0.01 } }}
                            required
                          />
                          <Box
                            sx={{
                              flex: 1,
                              minWidth: 120,
                              px: 2,
                              py: 1,
                              borderRadius: 1.5,
                              background: `linear-gradient(135deg, ${accent}14 0%, ${accent}07 100%)`,
                              border: `1px solid ${accent}30`,
                              display: 'flex',
                              flexDirection: 'column',
                              justifyContent: 'center',
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{
                                color: 'rgba(255,255,255,0.4)',
                                fontSize: '0.6rem',
                                letterSpacing: '0.4px',
                              }}
                            >
                              SUBTOTAL
                            </Typography>
                            <Typography
                              variant="body2"
                              fontWeight={800}
                              sx={{ color: accent, fontFamily: 'monospace' }}
                            >
                              GH₵{' '}
                              {subtotal.toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}
                            </Typography>
                          </Box>
                        </Box>
                      </Paper>
                    );
                  })}
                </Box>
              </Box>
            </Box>
          </Box>

          {/* Grand total bar */}
          <Box
            sx={{
              px: 3,
              py: 2.5,
              borderTop: '1px solid rgba(255,255,255,0.06)',
              background:
                'linear-gradient(135deg, rgba(255,152,0,0.12) 0%, rgba(255,152,0,0.04) 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Box>
              <Typography
                variant="caption"
                sx={{
                  color: 'rgba(255,255,255,0.45)',
                  letterSpacing: '0.5px',
                  fontSize: '0.62rem',
                  textTransform: 'uppercase',
                  display: 'block',
                }}
              >
                Grand Total
              </Typography>
              <Typography
                variant="h5"
                fontWeight={900}
                sx={{ color: '#ff9800', lineHeight: 1, fontFamily: 'monospace' }}
              >
                GH₵{' '}
                {overallTotal.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </Typography>
            </Box>
            <Typography
              variant="body2"
              sx={{ color: 'rgba(255,255,255,0.35)', textAlign: 'right', fontSize: '0.78rem' }}
            >
              {totalBirdsInForm.toLocaleString()} birds
              <br />
              <span style={{ color: 'rgba(255,255,255,0.2)' }}>
                across {birdEntries.length} strain{birdEntries.length !== 1 ? 's' : ''}
              </span>
            </Typography>
          </Box>
        </DialogContent>

        <DialogActions
          sx={{ px: 3, py: 2, gap: 1.5, borderTop: '1px solid rgba(255,255,255,0.04)' }}
        >
          <Button
            onClick={() => setAddDialog(false)}
            disabled={createMutation.isPending}
            sx={{
              color: 'rgba(255,255,255,0.45)',
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: 1.5,
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreateProcurement}
            variant="contained"
            disabled={createMutation.isPending}
            sx={{
              background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)',
              color: '#ffffff',
              borderRadius: 1.5,
              px: 3.5,
              textTransform: 'none',
              fontWeight: 700,
              boxShadow: '0 4px 14px rgba(255,152,0,0.3)',
              '&:hover': { background: 'linear-gradient(135deg, #ffa726 0%, #ff9800 100%)' },
              '&:disabled': { background: 'rgba(255,152,0,0.2)', color: 'rgba(255,255,255,0.3)' },
            }}
          >
            {createMutation.isPending ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              'Create Procurement'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ══════════════════════════════════════
          Delete Confirmation Dialog
      ══════════════════════════════════════ */}
      <Dialog
        open={deleteDialog}
        onClose={() => {
          setDeleteDialog(false);
          setProcurementToDelete(null);
        }}
        maxWidth="xs"
        fullWidth
        slotProps={{
          paper: {
            sx: {
              borderRadius: 3,
              background: '#0e0e10',
              border: '1px solid rgba(244,67,54,0.2)',
              backgroundImage: 'none',
            },
          },
        }}
      >
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: '50%',
              mx: 'auto',
              mb: 2,
              bgcolor: 'rgba(244,67,54,0.1)',
              border: '1px solid rgba(244,67,54,0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <WarningIcon sx={{ color: '#f44336', fontSize: 26 }} />
          </Box>
          <Typography variant="h6" fontWeight={700} sx={{ color: '#ffffff', mb: 0.5 }}>
            Delete Procurement?
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.45)', mb: 2.5 }}>
            This will permanently remove
          </Typography>
          <Paper
            elevation={0}
            sx={{
              py: 1.5,
              px: 2,
              mb: 2.5,
              background: 'rgba(244,67,54,0.07)',
              border: '1px solid rgba(244,67,54,0.2)',
              borderRadius: 2,
            }}
          >
            <Typography variant="body2" fontWeight={700} sx={{ color: '#ffffff' }}>
              {procurementToDelete?.name}
            </Typography>
          </Paper>
          <Typography
            variant="caption"
            sx={{ color: 'rgba(244,67,54,0.7)', display: 'block', mb: 3 }}
          >
            All associated birds, logs and expenditures will be removed. This cannot be undone.
          </Typography>
          <Box display="flex" gap={1.5} justifyContent="center">
            <Button
              onClick={() => {
                setDeleteDialog(false);
                setProcurementToDelete(null);
              }}
              disabled={deleteMutation.isPending}
              sx={{
                color: 'rgba(255,255,255,0.5)',
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: 1.5,
                px: 3,
                border: '1px solid rgba(255,255,255,0.1)',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.04)' },
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmDelete}
              variant="contained"
              disabled={deleteMutation.isPending}
              sx={{
                background: 'linear-gradient(135deg, #f44336 0%, #e53935 100%)',
                color: '#ffffff',
                borderRadius: 1.5,
                px: 3,
                textTransform: 'none',
                fontWeight: 700,
                boxShadow: '0 4px 14px rgba(244,67,54,0.3)',
                '&:hover': { background: 'linear-gradient(135deg, #e53935 0%, #c62828 100%)' },
                '&:disabled': { background: 'rgba(244,67,54,0.2)', color: 'rgba(255,255,255,0.3)' },
              }}
            >
              {deleteMutation.isPending ? <CircularProgress size={18} color="inherit" /> : 'Delete'}
            </Button>
          </Box>
        </Box>
      </Dialog>

      {/* ══════════════════════════════════════
          Mark as Delivered Dialog
      ══════════════════════════════════════ */}
      <Dialog
        open={deliveryDialog}
        onClose={handleCloseDeliveryDialog}
        maxWidth="sm"
        fullWidth
        slotProps={{
          paper: {
            sx: {
              borderRadius: 3.5,
              background: '#0e0e10',
              border: '1px solid rgba(76,175,80,0.2)',
              backgroundImage: 'none',
            },
          },
        }}
      >
        {/* Dialog header */}
        <Box
          sx={{
            px: 3,
            pt: 3,
            pb: 2.5,
            borderBottom: '1px solid rgba(76,175,80,0.1)',
            background: 'linear-gradient(135deg, rgba(76,175,80,0.06) 0%, transparent 100%)',
            display: 'flex',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: 2,
              bgcolor: 'rgba(76,175,80,0.1)',
              border: '1px solid rgba(76,175,80,0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <LocalShippingIcon sx={{ color: '#4caf50', fontSize: 22 }} />
          </Box>
          <Box flex={1}>
            <Typography
              variant="h6"
              fontWeight={800}
              sx={{ color: '#ffffff', letterSpacing: '-0.01em' }}
            >
              Record Delivery
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.38)' }}>
              {procurementToDeliver?.identifier}
            </Typography>
          </Box>
          {deliveryBirds.length > 0 && (
            <Box sx={{ textAlign: 'right' }}>
              <Typography
                variant="caption"
                sx={{
                  color: 'rgba(255,255,255,0.3)',
                  display: 'block',
                  fontSize: '0.6rem',
                  letterSpacing: '0.5px',
                  textTransform: 'uppercase',
                }}
              >
                Total to deliver
              </Typography>
              <Typography variant="h6" fontWeight={800} sx={{ color: '#4caf50', lineHeight: 1 }}>
                {deliveryBirds.reduce((s, b) => s + b.quantity_delivered, 0).toLocaleString()}
              </Typography>
            </Box>
          )}
        </Box>

        <DialogContent sx={{ p: 3, maxHeight: '54vh', overflowY: 'auto' }}>
          {deliveryBirds.length === 0 ? (
            <Alert severity="info" sx={{ borderRadius: 2 }}>
              No bird strains found for this procurement.
            </Alert>
          ) : (
            <Box display="flex" flexDirection="column" gap={1.5}>
              {deliveryBirds.map((bird, index) => {
                const accent = bird.color === 'white' ? '#42a5f5' : '#a1887f';
                const pct =
                  bird.quantity_bought > 0
                    ? Math.round((bird.quantity_delivered / bird.quantity_bought) * 100)
                    : 0;

                return (
                  <Paper
                    key={index}
                    elevation={0}
                    sx={{
                      p: 2.5,
                      border: `1px solid ${accent}22`,
                      borderLeft: `3px solid ${accent}`,
                      borderRadius: 2,
                      background: `${accent}06`,
                    }}
                  >
                    <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography
                          variant="body2"
                          fontWeight={700}
                          sx={{ color: accent, fontSize: '0.83rem' }}
                        >
                          {bird.color.charAt(0).toUpperCase() + bird.color.slice(1)} ·{' '}
                          {bird.birds_type}
                        </Typography>
                      </Box>
                      <Box display="flex" alignItems="center" gap={0.75}>
                        <Typography
                          variant="caption"
                          sx={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.7rem' }}
                        >
                          {bird.supplier_name}
                        </Typography>
                        <Chip
                          label={`${pct}%`}
                          size="small"
                          sx={{
                            height: 18,
                            fontSize: '0.6rem',
                            fontWeight: 700,
                            bgcolor: `${accent}18`,
                            color: accent,
                            border: `1px solid ${accent}30`,
                          }}
                        />
                      </Box>
                    </Box>

                    <Box display="flex" gap={1.5} flexWrap="wrap">
                      <TextField
                        label="Delivery Date"
                        type="date"
                        size="small"
                        value={bird.date_delivered}
                        onChange={(e) => {
                          const updated = [...deliveryBirds];
                          updated[index] = { ...updated[index], date_delivered: e.target.value };
                          setDeliveryBirds(updated);
                        }}
                        slotProps={{ inputLabel: { shrink: true } }}
                        sx={{
                          flex: 1,
                          minWidth: 148,
                          '& .MuiOutlinedInput-root': { borderRadius: 1.5 },
                        }}
                      />
                      <TextField
                        label="Qty Delivered"
                        type="number"
                        size="small"
                        value={bird.quantity_delivered}
                        onChange={(e) => {
                          const updated = [...deliveryBirds];
                          updated[index] = {
                            ...updated[index],
                            quantity_delivered: parseInt(e.target.value) || 0,
                          };
                          setDeliveryBirds(updated);
                        }}
                        slotProps={{ htmlInput: { min: 0, max: bird.quantity_bought } }}
                        helperText={`Ordered: ${bird.quantity_bought.toLocaleString()}`}
                        sx={{
                          flex: 1,
                          minWidth: 120,
                          '& .MuiOutlinedInput-root': { borderRadius: 1.5 },
                        }}
                      />
                    </Box>

                    {/* Mini progress bar */}
                    <Box sx={{ mt: 1.5 }}>
                      <LinearProgress
                        variant="determinate"
                        value={Math.min(pct, 100)}
                        sx={{
                          height: 3,
                          borderRadius: 2,
                          bgcolor: `${accent}18`,
                          '& .MuiLinearProgress-bar': { background: accent, borderRadius: 2 },
                        }}
                      />
                    </Box>
                  </Paper>
                );
              })}
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, gap: 1.5, borderTop: '1px solid rgba(76,175,80,0.08)' }}>
          <Button
            onClick={handleCloseDeliveryDialog}
            disabled={markAsDeliveredMutation.isPending}
            sx={{
              color: 'rgba(255,255,255,0.45)',
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: 1.5,
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelivery}
            variant="contained"
            disabled={markAsDeliveredMutation.isPending}
            sx={{
              background: 'linear-gradient(135deg, #4caf50 0%, #388e3c 100%)',
              color: '#ffffff',
              borderRadius: 1.5,
              px: 3.5,
              textTransform: 'none',
              fontWeight: 700,
              boxShadow: '0 4px 14px rgba(76,175,80,0.3)',
              '&:hover': { background: 'linear-gradient(135deg, #66bb6a 0%, #4caf50 100%)' },
              '&:disabled': { background: 'rgba(76,175,80,0.2)', color: 'rgba(255,255,255,0.3)' },
            }}
          >
            {markAsDeliveredMutation.isPending ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              'Confirm Delivery'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Snackbar ── */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          sx={{ width: '100%', borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.4)' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ProcurementsPage;
